import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import './index.css';
import Home from './Homepage';
import About from './About';
import Services from './Services';
import Contact from './Contact';
import Fleet from './Fleet';
import Download from './Download';
import Policy from './Policy';
import FAQ from './FreqAsked'; // Import the FAQ component
import a2bLogo from './Pictures/newlogo.png';

function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 950);
  const [isHalfDesktop, setIsHalfDesktop] = useState(window.innerWidth >= 950 && window.innerWidth < 1050);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 950);
      setIsHalfDesktop(window.innerWidth >= 950 && window.innerWidth < 1050);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Router>
      <AppContent isMobile={isMobile} isHalfDesktop={isHalfDesktop} />
    </Router>
  );
}

function AppContent({ isMobile, isHalfDesktop }) {
  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<MainContent isMobile={isMobile} isHalfDesktop={isHalfDesktop} />} />
        <Route path="/policy" element={<Policy />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

function MainContent({ isMobile, isHalfDesktop }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCitiesOpen, setIsCitiesOpen] = useState(false); // Add state for cities dropdown

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleCities = () => setIsCitiesOpen(!isCitiesOpen); // Add toggle function for cities dropdown

  // Calculate navbar height based on device type
  const navHeight = isMobile || isHalfDesktop ? '100px' : '60px';

  return (
    <>
      {/* Navbar */}
      <nav className={`bg-white backdrop-blur-md py-3 fixed top-0 left-0 right-0 z-50 shadow-lg ${isMobile ? 'mobile-navbar' : isHalfDesktop ? 'half-desktop-navbar' : 'hidden lg:block'}`}>
        <div className="flex items-center justify-between px-4">
          <div className="flex items-center">
            <img src={a2bLogo} alt="A2B Logo" className={`cursor-pointer ${isMobile ? 'h-6' : isHalfDesktop ? 'h-8' : 'h-8'} ${isHalfDesktop || !isMobile ? 'pl-20' : ''}`} onClick={() => {
              window.location.hash = '#top';
              window.location.reload();
            }} />
            {isMobile && <span className="ml-2 text-black font-medium text-sm">Luxury Ground Transportation</span>}
          </div>
          {(isMobile || isHalfDesktop) ? (
            <button className="text-black focus:outline-none" onClick={toggleMenu}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path>
              </svg>
            </button>
          ) : (
            <ul className="flex items-center space-x-2 md:space-x-6">
              <li className="text-center md:text-left">
                <a href="#top" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Home</a>
              </li>
              <li className="relative group text-center md:text-left">
                <button onClick={toggleCities} className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300 flex items-center">
                  Cities
                  <svg 
                    className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isCitiesOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>
                {isCitiesOpen && (
                  <ul className={`fixed left-0 right-0 top-[${navHeight}] mt-0 min-h-[400px] bg-white/90 backdrop-blur-md shadow-lg transition-all duration-300 cities-dropdown`}>
                    <div className="container mx-auto px-4 py-6 grid grid-cols-4 gap-6">
                      {[
                        'Singapore', 'Paris', 'Athens', 'Sydney', 'Rome', 'Barcelona', 'London', 
                        'Washington DC', 'Tampa', 'Seattle', 'Phoenix', 'Orlando', 'New York City', 
                        'New Orleans', 'Miami', 'Atlanta', 'Baltimore', 'Charlotte', 'Chicago', 
                        'Denver', 'Hartford', 'Houston', 'Las Vegas', 'Los Angeles', 'Memphis'
                      ].map(city => (
                        <li key={city} className="px-4 py-3 hover:bg-black/5 transition-colors duration-200">
                          <span className="block text-black/80 text-sm font-medium">{city}</span>
                        </li>
                      ))}
                    </div>
                  </ul>
                )}
              </li>
              <li className="text-center md:text-left">
                <a href="#fleet" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Fleet</a>
              </li>
              <li className="text-center md:text-left whitespace-nowrap">
                <a href="#about" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">About Us</a>
              </li>
              <li className="text-center md:text-left">
                <a href="#services" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Services</a>
              </li>
              <li className="text-center md:text-left">
                <a href="#faq" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">FAQ</a>
              </li>
              <li className="text-center md:text-left">
                <a href="#contact" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Contact</a>
              </li>
            </ul>
          )}
        </div>
        {(isMobile || isHalfDesktop) && isOpen && (
          <ul className="flex flex-col items-center justify-center space-y-2 mt-2">
            <li className="text-center p-0 m-0">
              <a href="#top" className="text-black font-medium py-2 px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Home</a>
            </li>
            
            <li className="text-center p-0 m-0">
              <a href="#fleet" className="text-black font-medium py-2 px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Fleet</a>
            </li>
            <li className="text-center p-0 m-0">
              <a href="#about" className="text-black font-medium py-2 px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">About Us</a>
            </li>
            <li className="text-center p-0 m-0">
              <a href="#services" className="text-black font-medium py-2 px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Services</a>
            </li>
            <li className="text-center md:text-left">
              <a href="#faq" className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">FAQ</a>
            </li>
            <li className="text-center p-0 m-0">
              <a href="#contact" className="text-black font-medium py-2 px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300">Contact</a>
            </li>
            <li className="relative group text-center md:text-left">
              <button onClick={toggleCities} className="text-black font-medium py-2 px-2 md:px-4 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-300 flex items-center">
                Cities
                <svg 
                  className={`w-4 h-4 ml-1 transform transition-transform duration-200 ${isCitiesOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              {isCitiesOpen && (
                <ul className={`fixed left-0 right-0 top-[${navHeight}] mt-0 min-h-[400px] bg-white/90 backdrop-blur-md shadow-lg transition-all duration-300 cities-dropdown`}>
                  <div className="container mx-auto px-4 py-6 grid grid-cols-4 gap-6">
                    {[
                      'Singapore', 'Paris', 'Athens', 'Sydney', 'Rome', 'Barcelona', 'London', 
                      'Washington DC', 'Tampa', 'Seattle', 'Phoenix', 'Orlando', 'New York City', 
                      'New Orleans', 'Miami', 'Atlanta', 'Baltimore', 'Charlotte', 'Chicago', 
                      'Denver', 'Hartford', 'Houston', 'Las Vegas', 'Los Angeles', 'Memphis'
                    ].map(city => (
                      <li key={city} className="px-4 py-3 hover:bg-black/5 transition-colors duration-200">
                        <span className="block text-black/80 text-sm font-medium">{city}</span>
                      </li>
                    ))}
                  </div>
                </ul>
              )}
            </li>
          </ul>
        )}
      </nav>

      <section id="home" className="pt-0">  
        <Home />
      </section>

      <section id="download" className="download-section bg-[#100E09] text-white text-left relative">
        <Download />
      </section>

      <section id="fleet" className="page-section scroll-adjust bg-white text-black py-8 relative z-10 shadow-lg">
        <Fleet />
      </section>

      <section id="about" className="page-section scroll-adjust bg-white text-black py-8 relative z-10 shadow-lg">
        <About />
      </section>

      <section id="services" className="page-section scroll-adjust bg-white text-black py-8 relative z-10 shadow-lg">
        <Services />
      </section>

      <section id="faq" className="page-section scroll-adjust bg-white text-black py-8 relative z-10 shadow-lg">
        <FAQ />
      </section>

      <section id="contact" className="page-section bg-[#100E09] text-white py-8">
        <Contact />
      </section>
    </>
  );
}

export default App;





